/**
 * GUIDES PAGE
 * 
 * Future features:
 * - Photo upload tips
 * - Color tagging guide
 * - Collection organization tips
 * - Marketplace best practices
 */

import { BookOpen, Camera, Palette, FolderOpen, ShoppingBag, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/common';

interface Guide {
  icon: React.ReactNode;
  title: string;
  description: string;
  comingSoon?: boolean;
}

const guides: Guide[] = [
  {
    icon: <Camera className="icon-base" />,
    title: 'Taking Great Swatch Photos',
    description: 'Learn how to capture beautiful, accurate photos of your nail polish that show true colors and finishes.',
    comingSoon: true
  },
  {
    icon: <Palette className="icon-base" />,
    title: 'Color Tagging & Organization',
    description: 'Master the art of categorizing your collection with our color picker and tagging system.',
    comingSoon: true
  },
  {
    icon: <FolderOpen className="icon-base" />,
    title: 'Organizing Your Collection',
    description: 'Tips and tricks for keeping your collection organized, from storage solutions to digital management.',
    comingSoon: true
  },
  {
    icon: <ShoppingBag className="icon-base" />,
    title: 'Marketplace Best Practices',
    description: 'How to create great destash listings, price items fairly, and communicate with buyers.',
    comingSoon: true
  },
  {
    icon: <Sparkles className="icon-base" />,
    title: 'Using the Discover Features',
    description: 'Get the most out of our Dupe Finder, ISO Matchmaker, and Color Explorer tools.',
    comingSoon: true
  }
];

export default function GuidesPage() {
  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* HEADER */}
        <PageHeader
          icon={BookOpen}
          title="Guides & Tutorials"
          subtitle="Learn how to get the most out of Polish ALL the Things"
          className="mb-6"
        />

        {/* Coming Soon Banner */}
        <div className="glass-card p-6 mb-8 text-center">
          <span className="inline-block px-4 py-2 bg-slate-700 text-glass-secondary rounded-full text-sm font-bold mb-3">
            ✨ Coming Soon
          </span>
          <p className="text-glass-secondary font-body">
            We're working on comprehensive guides to help you make the most of your collection. Check back soon!
          </p>
        </div>

        {/* Guide Cards */}
        <div className="grid gap-4">
          {guides.map((guide, index) => (
            <div 
              key={index}
              className="glass-card p-6 hover:bg-slate-800/60 transition-default"
            >
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl rainbow-gradient flex items-center justify-center text-white flex-shrink-0">
                  {guide.icon}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-bold text-glass font-body">
                      {guide.title}
                    </h3>
                    {guide.comingSoon && (
                      <span className="px-2 py-0.5 bg-slate-800/80 text-glass text-xs rounded-full font-semibold">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <p className="text-glass-secondary font-body">
                    {guide.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Tips Section */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-glass mb-6 text-center font-heading">
            Quick Tips
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="glass-card-sm p-5">
              <h3 className="font-bold text-glass mb-2 flex items-center gap-2">
                📸 Photo Tip
              </h3>
              <p className="text-sm text-glass-secondary">
                Natural daylight gives the most accurate color representation. Avoid direct sunlight which can wash out colors.
              </p>
            </div>
            <div className="glass-card-sm p-5">
              <h3 className="font-bold text-glass mb-2 flex items-center gap-2">
                🏷️ Tagging Tip
              </h3>
              <p className="text-sm text-glass-secondary">
                Use specific finish tags like "linear holo", "scattered holo", "multichrome" rather than just "shimmer".
              </p>
            </div>
            <div className="glass-card-sm p-5">
              <h3 className="font-bold text-glass mb-2 flex items-center gap-2">
                💰 Pricing Tip
              </h3>
              <p className="text-sm text-glass-secondary">
                Check the Marketplace to see what similar items are selling for before pricing your destash items.
              </p>
            </div>
            <div className="glass-card-sm p-5">
              <h3 className="font-bold text-glass mb-2 flex items-center gap-2">
                🔍 Search Tip
              </h3>
              <p className="text-sm text-glass-secondary">
                Use the Dupe Finder to discover similar polishes you already own before buying new ones!
              </p>
            </div>
          </div>
        </div>

        {/* Link to Help */}
        <div className="mt-12 text-center">
          <p className="text-on-bg mb-4 font-body">
            Have a specific question?
          </p>
          <Link
            to="/help"
            className="inline-flex items-center gap-2 px-6 py-3 btn-primary rounded-xl font-medium transition-default font-body"
          >
            Visit the FAQ
          </Link>
        </div>
      </div>
    </div>
  );
}
